import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { subscribe, purge } from "./toastStore";
import { toast, configure, POSITION } from "./toast";
import { toastTitle, toastDismissLabel, ensureToastMessages } from "./toastMessages";

const EXIT_MS = 260;

const ICONS = {
  success: "bi-check-lg",
  error: "bi-x-lg",
  warning: "bi-exclamation-lg",
  info: "bi-info-lg",
  default: "bi-bell-fill",
};

const resolveContent = (content, ctx) => {
  if (typeof content === "function") {
    try {
      return content(ctx);
    } catch (e) {
      return null;
    }
  }
  return content;
};

function ToastCard({ item }) {
  const {
    id,
    type,
    content,
    title,
    autoClose,
    closing,
    nonce,
    pauseOnHover,
    closeOnClick,
    hideProgressBar,
    closeButton,
    icon,
    action,
    count,
    className,
    onClose,
    data,
  } = item;

  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  const remainingRef = useRef(0);
  const startedRef = useRef(0);
  const pauseFnRef = useRef(null);
  const resumeFnRef = useRef(null);

  // Auto-dismiss timer. Re-armed whenever the card is updated (nonce) or its duration changes.
  useEffect(() => {
    if (closing || autoClose === false) return undefined;
    remainingRef.current = autoClose;
    let timer = null;

    const arm = () => {
      startedRef.current = Date.now();
      timer = setTimeout(() => toast.dismiss(id), remainingRef.current);
    };
    const disarm = () => {
      if (timer === null) return;
      clearTimeout(timer);
      timer = null;
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedRef.current));
    };

    pauseFnRef.current = disarm;
    resumeFnRef.current = arm;
    if (!pausedRef.current) arm();

    return () => {
      if (timer !== null) clearTimeout(timer);
      pauseFnRef.current = null;
      resumeFnRef.current = null;
    };
  }, [id, autoClose, nonce, closing]);

  // Remove from the store after the exit animation has played.
  useEffect(() => {
    if (!closing) return undefined;
    const t = setTimeout(() => {
      purge(id);
      if (onClose) {
        try {
          onClose(data);
        } catch (e) {
          /* ignore */
        }
      }
    }, EXIT_MS);
    return () => clearTimeout(t);
  }, [closing, id, onClose, data]);

  const handleEnter = () => {
    if (!pauseOnHover) return;
    pausedRef.current = true;
    setPaused(true);
    pauseFnRef.current?.();
  };

  const handleLeave = () => {
    if (!pauseOnHover) return;
    pausedRef.current = false;
    setPaused(false);
    resumeFnRef.current?.();
  };

  const body = resolveContent(content, { closeToast: () => toast.dismiss(id), data, toastProps: item });
  const heading = title || toastTitle(type);
  const isLoading = type === "loading";
  const showIcon = icon !== false;

  const classes = [
    "pp-toast",
    `pp-toast--${type}`,
    closing ? "is-closing" : "is-open",
    closeOnClick ? "is-clickable" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={classes}
      role={type === "error" || type === "warning" ? "alert" : "status"}
      aria-live={type === "error" ? "assertive" : "polite"}
      aria-atomic="true"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onClick={closeOnClick ? () => toast.dismiss(id) : undefined}
    >
      <span className="pp-toast__glow" aria-hidden="true" />

      {showIcon && (
        <div className="pp-toast__icon" aria-hidden="true">
          {icon ? (
            icon
          ) : isLoading ? (
            <span className="pp-toast__spinner" />
          ) : (
            <i className={`bi ${ICONS[type] || ICONS.default}`} />
          )}
        </div>
      )}

      <div className="pp-toast__body">
        <div className="pp-toast__title">
          <span>{heading}</span>
          {count > 1 && <span className="pp-toast__count">×{count}</span>}
        </div>
        {body !== null && body !== undefined && body !== "" && (
          <div className="pp-toast__message">{body}</div>
        )}
        {action && (
          <button
            type="button"
            className="pp-toast__action"
            onClick={(e) => {
              e.stopPropagation();
              try {
                action.onClick?.(e);
              } finally {
                if (action.dismissOnClick !== false) toast.dismiss(id);
              }
            }}
          >
            {action.label}
          </button>
        )}
      </div>

      {closeButton && (
        <button
          type="button"
          className="pp-toast__close"
          aria-label={toastDismissLabel()}
          onClick={(e) => {
            e.stopPropagation();
            toast.dismiss(id);
          }}
        >
          <i className="bi bi-x-lg" />
        </button>
      )}

      {autoClose !== false && !hideProgressBar && !closing && (
        <span
          key={nonce}
          className="pp-toast__progress"
          aria-hidden="true"
          style={{ animationDuration: `${autoClose}ms`, animationPlayState: paused ? "paused" : "running" }}
        />
      )}
    </div>
  );
}

/**
 * Mount once near the app root. Accepts a subset of react-toastify's <ToastContainer /> props
 * (position, limit, newestOnTop); everything else is ignored for compatibility.
 */
export default function AppToastHost({ position = POSITION.TOP_CENTER, limit, newestOnTop = true }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    ensureToastMessages();
    return subscribe(setItems);
  }, []);

  useEffect(() => {
    configure({ position, limit });
  }, [position, limit]);

  const groups = useMemo(() => {
    const map = {};
    items.forEach((t) => {
      const key = t.position || position;
      (map[key] = map[key] || []).push(t);
    });
    return map;
  }, [items, position]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <>
      {Object.entries(groups).map(([pos, list]) => {
        const ordered = newestOnTop && pos.startsWith("top") ? [...list].reverse() : list;
        return (
          <div key={pos} className={`pp-toast-region pp-toast-region--${pos}`}>
            {ordered.map((t) => (
              <ToastCard key={t.id} item={t} />
            ))}
          </div>
        );
      })}
    </>,
    document.body
  );
}
